import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BoundedContent,
    Column,
    GridContainer,
    H2,
    Li,
    Link,
    ListPrice,
    P,
    Row,
    Spinner,
    Ul,
} from '@vp/swan';
import { useLogger } from '@vp/ubik-context';
import { Logger } from '@vp/ubik-logging';
import FAQAccordion from '../../../../components/FAQAccordion/FAQAccordion';
import { sanitiseLocale } from '../../../../i18n/config';
import { useEnvContext } from '../../../../utils/environmentContext';
import { isGenAiLogomakerAvailable } from '../../../../utils/featureAvailability';
import { markdownToSanitizedText } from '../../../../utils/markdownToSanitizedText';
import {
    getDesignableProductPrice,
    logoProductsPricing,
    priceFromFloat,
} from '../../../../utils/pricing';
import { reactNodeToSanitizedText } from '../../../../utils/reactNodeToSanitizedText';
import { ninetyNineMoneyBackGuaranteeUrl, proLogoDesignUrl } from '../../../../utils/urls';
import type { FAQ, FAQSchema } from '../../../../components/FAQAccordion/FAQAccordion';

interface FAQProps {
    longNameSpaced?: string;
}

const Faq = ({ longNameSpaced }: FAQProps) => {
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const [logoEssentialListPrice, setLogoEssentialListPrice] = useState<string>('');
    const [isLoadingPrice, setIsLoadingPrice] = useState<boolean>(false);
    const logger: Logger = useLogger();
    const { ENV_API_BASE_URL } = useEnvContext();

    useEffect(() => {
        const fetchLogoEssentialPrice = async () => {
            setIsLoadingPrice(true);
            const price = await getDesignableProductPrice(
                'logoessential',
                'en-US',
                ENV_API_BASE_URL,
                logger,
            );
            const listPrice = price ? priceFromFloat(price.list_price.taxed, 'en-US') : '';
            setLogoEssentialListPrice(listPrice);
            setIsLoadingPrice(false);
        };
        fetchLogoEssentialPrice();
    }, [locale]);

    const logoContestBronzePrice = logoProductsPricing[locale]['logo-contest-bronze'];
    const premiumIconStartPrice = logoProductsPricing[locale]['logomaker-icon-premium-start-price'];
    const exclusiveIconPrice = logoProductsPricing[locale]['logomaker-icon-exclusive'];
    const logoEssentialPrice = isLoadingPrice ? (
        <Spinner size="tiny" accessibleText="Loading price" />
    ) : (
        <ListPrice>{logoEssentialListPrice}</ListPrice>
    );

    const faqData: FAQ[] = [
        {
            id: 'whats-the-difference',
            question:
                "What's the difference between a Logomaker logo and one created by a professional designer?",
            answer: (
                <Ul>
                    <Li>
                        <b>Logomaker by VistaPrint®</b> is a free tool that you can use to create,
                        customise, and download your logo. It uses complex AI and algorithms,
                        working with an expansive library of fonts and icons, to create a wide range
                        of visual {longNameSpaced}logo ideas and layout options. Simply tell
                        Logomaker about your business, and the tool taps into the library to create
                        a new {longNameSpaced}logo, just for you. If you need assistance while using
                        our Logomaker, our Customer Care specialists can help with any queries, such
                        as adjusting and reviewing your {longNameSpaced}logo. While the service is
                        free, you can choose to add &apos;premium icons&apos; starting from just{' '}
                        <ListPrice>{premiumIconStartPrice}</ListPrice>.
                    </Li>
                    <Li>
                        <b>Hiring a professional designer</b> starts at just {logoEssentialPrice}.
                        Through this service, you collaborate directly with a freelancer from the
                        99designs by Vista global community to create a unique, hand-crafted{' '}
                        {longNameSpaced}logo that matches your vision. You start by choosing a
                        designer to work with, fill out a quick brief with details about your idea
                        (even add in some logo inspiration as a reference!) and then collaborate
                        until you&apos;re happy with the result.{' '}
                        <Link href={proLogoDesignUrl(locale)}>
                            You can learn more about logo design services here.
                        </Link>
                    </Li>
                    <Li>
                        <b>Logo design contest at 99designs by Vista</b> starts at{' '}
                        {logoContestBronzePrice}. Through this service, you&apos;ll receive dozens
                        of {longNameSpaced}logo ideas from our professional designers around the
                        world. You start by filling in a simple online brief where you describe
                        things like the colours you like (even add in some logo inspiration as a
                        reference!). Freelancers then share their {longNameSpaced}logo ideas and you
                        choose your favourite – or get your money back if none feel right.
                    </Li>
                </Ul>
            ),
        },
        {
            id: 'how-much-does-it-cost',
            question: `How much does it cost to create a ${longNameSpaced}logo with VistaPrint?`,
            answer: (
                <>
                    <P>VistaPrint offers logo creation options to fit any budget:</P>
                    <Ul>
                        <Li>
                            <b>Logomaker:</b> You can create your own {longNameSpaced}logo for free
                            using Logomaker by VistaPrint®. No design skills are required, and the{' '}
                            {longNameSpaced}logo will be ready for both digital and print use. If
                            you wish to add a &apos;premium icon&apos; to your Logomaker design,
                            these can be purchased from just{' '}
                            <ListPrice>{premiumIconStartPrice}</ListPrice> - or you can purchase an
                            &apos;exclusive icon&apos; outright for{' '}
                            <ListPrice>{exclusiveIconPrice}</ListPrice> which will result in the
                            removal of this icon from our library once you download it.
                        </Li>
                        <Li>
                            <b>Hire a Designer:</b> For a more customized approach, you can
                            collaborate with a professional freelancer for{' '}
                            <ListPrice>{logoEssentialPrice}</ListPrice> to create a unique{' '}
                            {longNameSpaced}logo tailored to your brand.
                        </Li>
                        <Li>
                            <b>Logo Contest:</b> Starting at {logoContestBronzePrice}, you can get
                            dozens of custom {longNameSpaced}logo ideas from our global design
                            community. Choose your favorite, or get your money back if none are the
                            right fit.
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'how-long-does-it-take',
            question: `How long does it take to get my ${longNameSpaced}logo?`,
            answer: (
                <>
                    <P>Turnaround times vary depending on the option you choose:</P>
                    <Ul>
                        <Li>
                            <b>Logomaker:</b> Multiple {longNameSpaced}logo ideas are instantly
                            generated by our Logomaker, which you can then refine and download once
                            you&apos;re happy with the result.
                        </Li>
                        <Li>
                            <b>Hire a Designer:</b> Typically, it takes 3–5 business days to receive
                            your completed {longNameSpaced}logo, depending on the complexity of the
                            design and the number of revisions.
                        </Li>
                        <Li>
                            <b>Logo Contest:</b> The process usually takes 7–10 business days,
                            allowing time for designers to submit ideas and for you to choose the{' '}
                            {longNameSpaced}logo you love.
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'similar-logo',
            question: `How can I get a ${longNameSpaced}logo similar to one I like?`,
            answer: (
                <>
                    <P>
                        If you&apos;ve found a {longNameSpaced}logo or logo inspiration from a
                        design that you love, there are several ways that we can help you create
                        something that captures a similar feel, while ensuring it&apos;s unique to
                        your brand. We suggest working one-on-one with our professional designer
                        community, either directly (Hiring a Designer) or through a Design
                        Contest—getting multiple ideas submitted from our community and choosing
                        your favorite {longNameSpaced}logo. During the &apos;brief&apos; stage,
                        simply share images of the logos you like, along with your vision, and
                        they&apos;ll craft a custom {longNameSpaced}logo that reflects those
                        elements while making it one-of-a-kind for your business.
                    </P>
                    <P>
                        If you prefer a do-it-yourself approach or are on a tighter budget, you can
                        use our free Logomaker to bring your logo inspiration to life and create
                        your own {longNameSpaced}logo. You might find a design that perfectly suits
                        your brand&apos;s style!
                    </P>
                </>
            ),
        },
        {
            id: 'is-logomaker-free',
            question: 'Is the Logomaker by VistaPrint® free?',
            answer: (
                <>
                    <P>
                        Yes! Logomaker offers you the ability to create, customize and download your{' '}
                        {longNameSpaced}logo for free. After creating your logo, you&apos;ll receive
                        high-resolution file formats that can be added to your Brand Kit and
                        &apos;My Projects&apos; folder, ready to use online and in printed marketing
                        materials.
                    </P>
                    <P>
                        We also offer additional {longNameSpaced}icon options through Logomaker,
                        which you can use in your {longNameSpaced}logo for a small fee. These icons
                        are handcrafted by our global community of designers and are available as
                        follows:
                    </P>
                    <Ul>
                        <Li>
                            <b>Premium Icons:</b> Use these icons in your Logomaker design for{' '}
                            <ListPrice>{premiumIconStartPrice}</ListPrice>. They will continue to be
                            available in our icon library.
                        </Li>
                        <Li>
                            <b>Exclusive Icons:</b> Purchase these icons outright for{' '}
                            <ListPrice>{exclusiveIconPrice}</ListPrice> and use them in your
                            Logomaker design. Once downloaded, the icon will be permanently removed
                            from our library.
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'file-types',
            question: `What kind of files do I get with my ${longNameSpaced}logo?`,
            answer: (
                <>
                    <P>
                        The types of logo files you receive depend on the method you choose for
                        creation. However, all of the logo files supplied will be versatile across
                        various platforms and formats, whether online or in print.
                    </P>

                    <Ul>
                        <Li>
                            <b>Logomaker by VistaPrint®:</b> After creating your {longNameSpaced}
                            logo, you&apos;ll receive 6 full-color files (3 with white backgrounds
                            and 3 with transparent backgrounds) in the following formats:
                            <Ul>
                                <Li>
                                    <b>SVG:</b> A vector graphic that can be resized without losing
                                    quality.
                                </Li>
                            </Ul>
                            <Ul>
                                <Li>
                                    <b>PNG:</b> A raster graphic ideal for websites and social media
                                    posts.
                                </Li>
                            </Ul>
                            <Ul>
                                <Li>
                                    <b>PDF:</b> A printable format that can be viewed without
                                    proprietary software.
                                </Li>
                            </Ul>
                        </Li>
                        <Li>
                            <b>Hiring a Designer or Logo Contest:</b> You&apos;ll receive
                            high-resolution, industry-standard file formats for the finalized{' '}
                            {longNameSpaced}logo, including:
                            <Ul>
                                <Li>
                                    <b>EPS (RGB color format):</b> For web use.
                                </Li>
                                <Li>
                                    <b>EPS (CMYK color format):</b> For print use.
                                </Li>
                                <Li>
                                    <b>JPG, PNG, PDF:</b> For general display purposes.
                                </Li>
                            </Ul>
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'do-I-own-my-logo',
            question: `Do I own my final ${longNameSpaced}logo design?`,
            answer: (
                <>
                    <P>It depends on which {longNameSpaced}logo design option you choose.</P>
                    <P>
                        If trademarking a logo is important to you, paying a professional freelancer
                        to create a bespoke {longNameSpaced}logo for you on 99designs by Vista is
                        the way to go. Whether you Hire a Designer or start a Contest, the finished
                        result will be a completely unique logo, that doesn&apos;t use any stock or
                        shared visual assets. You&apos;ll get all rights owned by the Designer in
                        the business logo you purchase transferred to you.
                    </P>
                    <P>
                        If you use our free Logomaker service, you&apos;ll be working with visuals
                        and {longNameSpaced}logo ideas that may be similar to others.
                    </P>
                </>
            ),
        },
        {
            id: 'why-create-a-logo',
            question: `Why create a ${longNameSpaced}logo with VistaPrint?`,
            answer: (
                <>
                    <P>
                        Millions of our customers have turned their new business idea into reality
                        with VistaPrint as a partner. It&apos;s an easy and affordable way to create
                        a standout logo and brand, get online with an easy to build website, and
                        launch and promote your business with an incredible range of print products
                        to help generate buzz.
                    </P>
                    <P>
                        We also provide expert logo help and customer support to guide you (even
                        after hours), or they can simply help you with choosing logo inspiration if
                        you&apos;re unsure where to start. You can also access your design and print
                        files at any time, all in the one place.
                    </P>
                </>
            ),
        },
    ];

    const faqGenAiData: FAQ[] = [
        {
            id: 'whats-the-difference',
            question:
                "What's the difference between an AI Logomaker logo and one created by a professional designer?",
            answer: (
                <>
                    <P>
                        Each of our {longNameSpaced}logo design options offers you a unique way to
                        create a professional logo design.
                    </P>
                    <Ul>
                        <Li>
                            <b>AI Logomaker by VistaPrint:</b> The fast, free and easy DIY tool. Our
                            intuitive AI, purpose-built for businesses, makes it easy to explore,
                            create and refine a variety of custom {longNameSpaced}logo options in
                            just a few minutes - and it delivers perfect, print-ready files.
                        </Li>
                        <Li>
                            <b>Pro Logo Design:</b> One-on-one collaboration. Through this design
                            service, you collaborate directly with a freelance designer from the
                            99designs by Vista global community to create a unique, hand-crafted{' '}
                            {longNameSpaced}logo that matches your vision. You start by choosing a
                            designer to work with, fill out a quick brief with details about your
                            project, and then collaborate until you love the finished result.{' '}
                            <Link href={proLogoDesignUrl(locale)}>
                                Learn more about our Pro Logo Design service here.
                            </Link>
                        </Li>
                        <Li>
                            <b>Logo Design Contest at 99designs by Vista: </b>
                            Lots of options. You can describe what you're looking for and then open
                            it up to the whole 99designs by Vista freelance community. Professional
                            designers will share ideas, and you can pick your favorite. If you don't
                            find a {longNameSpaced}logo that meets your needs, we also offer a 100%
                            money-back guarantee.{' '}
                            <Link href={ninetyNineMoneyBackGuaranteeUrl()}>
                                You can find out more about our 100% money-back guarantee here.
                            </Link>
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'how-much-does-it-cost',
            question: `How much does it cost to create a ${longNameSpaced}logo with VistaPrint?`,
            answer: (
                <>
                    <P>VistaPrint offers logo creation options to fit any budget:</P>
                    <Ul>
                        <Li>
                            <b>AI Logomaker:</b> You can create your own {longNameSpaced}logo for
                            free using our AI Logomaker. No design skills are required, and you'll
                            receive free logo files ready for both print and digital use.
                        </Li>
                        <Li>
                            <b>Pro Logo Design:</b> For a more customized approach, you can
                            collaborate directly with a professional freelance designer for{' '}
                            <ListPrice>{logoEssentialPrice}</ListPrice> to create a handcrafted,
                            one-of-a-kind {longNameSpaced}logo tailored to your brand.
                        </Li>
                        <Li>
                            <b>Logo Design Contest via 99designs:</b> Starting at{' '}
                            {logoContestBronzePrice}, you can get dozens of custom {longNameSpaced}
                            logo ideas from our global designer community. Choose your favorite, or
                            get your money back if none are the right fit.{' '}
                            <Link href={ninetyNineMoneyBackGuaranteeUrl()}>
                                You can find out more about our 100% money-back guarantee here.
                            </Link>
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'how-long-does-it-take',
            question: `How long does it take to get my ${longNameSpaced}logo?`,
            answer: (
                <>
                    <P>Turnaround times vary depending on the option you choose:</P>
                    <Ul>
                        <Li>
                            <b>AI Logomaker:</b> Multiple {longNameSpaced}logo ideas are instantly
                            generated by our Logomaker, which you can then refine and download once
                            you're happy with the result.
                        </Li>
                        <Li>
                            <b>Pro Logo Design:</b> Typically, it takes 3-5 business days to receive
                            your completed {longNameSpaced}logo, depending on the complexity of the
                            design and the number of revisions.
                        </Li>
                        <Li>
                            <b>Logo Design Contest via 99designs:</b> The process usually takes 7-10
                            business days, allowing time for designers to submit ideas and for you
                            to choose the {longNameSpaced}logo you love.
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'similar-logo',
            question: `How can I get a ${longNameSpaced}logo similar to one I like?`,
            answer: (
                <>
                    <P>
                        If you've found a {longNameSpaced}logo or logo inspiration from a design
                        that you love, there are several ways that we can help you create something
                        that captures a similar feel, while ensuring it's unique to your brand. We
                        suggest working one-on-one with our professional designer community, either
                        directly (through Pro Logo Design) or through a Design Contest - getting
                        multiple ideas submitted from our community and choosing your favorite{' '}
                        {longNameSpaced}logo.
                    </P>
                    <P>
                        During the 'brief' stage, simply share images of the {longNameSpaced}logos
                        you like, along with your vision, and they'll hand craft a logo that
                        reflects those elements while making it one-of-a-kind for your business.
                    </P>
                    <P>
                        If you prefer a do-it-yourself approach or are on a tighter budget, you can
                        use our free AI Logomaker to bring your logo inspiration to life and create
                        your own {longNameSpaced}logo. You might find a design that perfectly suits
                        your brand's style!
                    </P>
                </>
            ),
        },
        {
            id: 'is-logomaker-free',
            question: 'Is the AI Logomaker by VistaPrint free?',
            answer: (
                <>
                    <P>
                        Yes! AI Logomaker by VistaPrint is 100% free to create, customize and
                        download your {longNameSpaced}logo. After creating your AI logo design,
                        you'll get high-resolution file formats and a Brand Kit that you can use
                        across your Vista projects - like social media posts, websites and printed
                        products.
                    </P>
                </>
            ),
        },
        {
            id: 'file-types',
            question: `What kind of files do I get with my ${longNameSpaced}logo?`,
            answer: (
                <>
                    <P>
                        The types of logo files you receive depend on the method you choose for
                        creation. However, all of the logo files supplied will be versatile across
                        various platforms and formats, whether online or in print.
                    </P>
                    <Ul>
                        <Li>
                            <b>AI Logomaker by VistaPrint:</b> After creating your {longNameSpaced}
                            logo, you'll receive 6 full-color files (3 with white backgrounds and 3
                            with transparent backgrounds) in the following formats:
                            <Ul>
                                <Li>
                                    <b>SVG:</b> A vector graphic that can be resized without losing
                                    quality.
                                </Li>
                            </Ul>
                            <Ul>
                                <Li>
                                    <b>PNG:</b> A raster graphic ideal for websites and social media
                                    posts.
                                </Li>
                            </Ul>
                            <Ul>
                                <Li>
                                    <b>PDF:</b> A printable format that can be viewed without
                                    proprietary software.
                                </Li>
                            </Ul>
                        </Li>
                        <Li>
                            <b>Pro Logo Design or Logo Contest:</b> You'll receive high-resolution,
                            industry-standard file formats for the finalized {longNameSpaced}logo,
                            including:
                            <Ul>
                                <Li>
                                    <b>EPS (RGB color format):</b> For web use.
                                </Li>
                                <Li>
                                    <b>EPS (CMYK color format):</b> For print use.
                                </Li>
                                <Li>
                                    <b>JPG, PNG, PDF:</b> For general display purposes.
                                </Li>
                            </Ul>
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'do-I-own-my-logo',
            question: `Do I own my final ${longNameSpaced}logo design?`,
            answer: (
                <>
                    <P>It depends on which logo design option you choose.</P>
                    <P>
                        If you use our free AI Logomaker, it will design entire {longNameSpaced}
                        logos without templates or stock. This makes your AI logo design unique to
                        your prompts and is safe for business or personal use - however,
                        AI-generated content is not considered to be the work of a human creator,
                        meaning it fails to meet the legal requirements for copyright protection in
                        many jurisdictions.
                    </P>
                    <P>
                        If this is important to you, paying a professional designer to create a
                        handcrafted {longNameSpaced}logo from scratch might be your best choice.
                        Whether you hire a designer (Pro Logo Design) or start a Design Contest via
                        99designs, the finished result will be a completely custom {longNameSpaced}
                        logo that can be copyrighted or trademarked.
                    </P>
                </>
            ),
        },
        {
            id: 'why-create-a-logo',
            question: `Why create a ${longNameSpaced}logo with VistaPrint?`,
            answer: (
                <>
                    <P>
                        Millions of our customers have turned their new business idea into reality
                        with VistaPrint as a partner. It's an easy and affordable way to create a
                        standout logo and brand, get online with an easy to build website, and
                        launch and promote your business with an incredible range of print products
                        to help generate buzz.
                    </P>
                    <P>
                        We also provide expert logo help and customer support to guide you (even
                        after hours), or they can simply help you with choosing logo inspiration if
                        you're unsure where to start. You can also access your design and print
                        files at any time, all in the one place.
                    </P>
                </>
            ),
        },
    ];

    const generateFaqSchemaData = (faqs: FAQ[]): FAQSchema[] => {
        return faqs.map((faq) => ({
            question: markdownToSanitizedText(faq.question as string),
            answer: reactNodeToSanitizedText(faq.answer as string),
        }));
    };

    return (
        <BoundedContent marginY="between-sections" id="faqs" as="section">
            <GridContainer>
                <Row>
                    <Column span={12} padding={0}>
                        <H2 fontSkin="title-section">Frequently asked questions</H2>
                        {isGenAiLogomakerAvailable(locale) ? (
                            <FAQAccordion
                                faqData={faqGenAiData}
                                faqSchemaData={generateFaqSchemaData(faqGenAiData)}
                            />
                        ) : (
                            <FAQAccordion
                                faqData={faqData}
                                faqSchemaData={generateFaqSchemaData(faqData)}
                            />
                        )}
                    </Column>
                </Row>
            </GridContainer>
        </BoundedContent>
    );
};

export default Faq;
